import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Col, Form, Select } from 'antd'
import { Country, State, City } from 'country-state-city'

// Match a stored name to a dataset entry tolerantly, so legacy values that differ
// slightly from the country-state-city names (e.g. "United States of America" vs
// "United States", "Czechia (Czech Republic)" vs "Czechia") still resolve and pre-fill
// the cascade. The stored value itself is left unchanged.
const COUNTRY_ALIASES = {
  usa: 'unitedstates',
  us: 'unitedstates',
  uk: 'unitedkingdom',
  uae: 'unitedarabemirates',
}
const norm = (s) => {
  const base = (s || '')
    .toString()
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/\bof america\b/g, '')
    .replace(/[^a-z]/g, '')
  return COUNTRY_ALIASES[base] || base
}
const matchByName = (list, val) =>
  val ? list.find((x) => x.name === val) || list.find((x) => norm(x.name) === norm(val)) : undefined

// Cascading Country -> State -> City selects for the profile forms, matching the
// CoreIQPro behavior (country-state-city package). Selecting a country loads its
// states; selecting a state loads its cities.
//
// Values are stored as NAMES (e.g. "United States", "California", "Los Angeles") to
// stay compatible with the existing 1Journey backend fields, which already hold the
// full names. The isoCodes are used only internally to look up the dependent lists.
const CountryStateCity = ({
  form,
  span = 8,
  required = true,
  countryName = 'country',
  stateName = 'state',
  cityName = 'city',
  countryLabel = 'Country',
  stateLabel = 'State',
  cityLabel = 'City',
}) => {
  const countries = useMemo(() => Country.getAllCountries(), [])

  const countryVal = Form.useWatch(countryName, form)
  const stateVal = Form.useWatch(stateName, form)

  const countryIso = useMemo(
    () => matchByName(countries, countryVal)?.isoCode,
    [countries, countryVal],
  )
  const states = useMemo(
    () => (countryIso ? State.getStatesOfCountry(countryIso) : []),
    [countryIso],
  )
  const stateIso = useMemo(() => matchByName(states, stateVal)?.isoCode, [states, stateVal])
  const cities = useMemo(
    () => (countryIso && stateIso ? City.getCitiesOfState(countryIso, stateIso) : []),
    [countryIso, stateIso],
  )

  // A country's dependents are only required when that list actually exists in the
  // dataset, so countries without states/cities do not block the form.
  const stateRule =
    required && states.length > 0 ? [{ required: true, message: 'State is required' }] : []
  const cityRule =
    required && cities.length > 0 ? [{ required: true, message: 'City is required' }] : []

  // The field's own value is set by the Form.Item binding; clear the dependents.
  const onCountry = () => form.setFieldsValue({ [stateName]: undefined, [cityName]: undefined })
  const onState = () => form.setFieldsValue({ [cityName]: undefined })

  return (
    <>
      <Col span={span}>
        <Form.Item
          label={countryLabel}
          name={countryName}
          rules={required ? [{ required: true, message: 'Country is required' }] : []}
        >
          <Select
            showSearch
            placeholder="Select Country"
            optionFilterProp="label"
            onChange={onCountry}
            options={countries.map((c) => ({ value: c.name, label: c.name }))}
          />
        </Form.Item>
      </Col>

      <Col span={span}>
        <Form.Item label={stateLabel} name={stateName} rules={stateRule}>
          <Select
            showSearch
            placeholder="Select State"
            optionFilterProp="label"
            disabled={!countryVal || states.length === 0}
            onChange={onState}
            notFoundContent={countryVal ? 'No states available' : 'Select a country first'}
            options={states.map((s) => ({ value: s.name, label: s.name }))}
          />
        </Form.Item>
      </Col>

      <Col span={span}>
        <Form.Item label={cityLabel} name={cityName} rules={cityRule}>
          <Select
            showSearch
            placeholder="Select City"
            optionFilterProp="label"
            disabled={!stateVal || cities.length === 0}
            notFoundContent={stateVal ? 'No cities available' : 'Select a state first'}
            options={cities.map((c) => ({ value: c.name, label: c.name }))}
          />
        </Form.Item>
      </Col>
    </>
  )
}

CountryStateCity.propTypes = {
  form: PropTypes.object.isRequired,
  span: PropTypes.number,
  required: PropTypes.bool,
  countryName: PropTypes.string,
  stateName: PropTypes.string,
  cityName: PropTypes.string,
  countryLabel: PropTypes.string,
  stateLabel: PropTypes.string,
  cityLabel: PropTypes.string,
}

export default CountryStateCity
